/**
 * Finance lists on DataTable v2 (T-41): pure helpers shared by Obligations,
 * Billing and Cash flow. Totals are computed on exactly the rows shown, so
 * the label "סה״כ" in the table's footer (`DataTable` `footer`) is honest.
 * Unit-tested (`src/utils/__tests__/financeModel.test.ts`).
 */

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

/* ------------------------------ Obligations ------------------------------ */

export interface ObligationLike {
  /** BQ-021: the approval stage; absent = live (older callers). */
  approval_status?: string | null;
  amount: number | string;
  total?: number | string | null;
  outstanding?: number | string | null;
  payment_status: string;
}

export interface ObligationTotals {
  count: number;
  /** Sum of the obligation totals (amount + adjustments). */
  amount: number;
  /** What is still owed on the unpaid ones. */
  outstanding: number;
  /** How many are not fully paid. */
  open: number;
}

/** The amount an obligation is for (after adjustments when the server sent them). */
export const obligationTotal = (o: ObligationLike) => num(o.total ?? o.amount);
/** What is still owed: 0 when paid; the server's outstanding, else the whole amount. */
export const obligationOutstanding = (o: ObligationLike) =>
  (o.payment_status === 'paid' ? 0 : num(o.outstanding ?? o.amount));

export function sumObligations(rows: readonly ObligationLike[]): ObligationTotals {
  return rows.reduce<ObligationTotals>((acc, o) => ({
    count: acc.count + 1,
    amount: acc.amount + obligationTotal(o),
    outstanding: acc.outstanding + obligationOutstanding(o),
    open: acc.open + (o.payment_status === 'paid' ? 0 : 1),
  }), { count: 0, amount: 0, outstanding: 0, open: 0 });
}

/**
 * BQ-021 (D-115, D-122): a charge does not exist towards the customer before the OTE manager
 * approves it. "Pending payment" / "open balance" count only approved / sent_to_morning
 * (like the debt aging); scheduled and awaiting-approval charges are shown apart.
 */
export const isLiveObligation = (o: ObligationLike) =>
  o.approval_status == null || o.approval_status === 'approved' || o.approval_status === 'sent_to_morning';

export function sumLiveObligations(rows: readonly ObligationLike[]): ObligationTotals {
  return sumObligations(rows.filter(isLiveObligation));
}

/** Charges waiting for the manager's approval (unpaid): how many and how much. */
export function sumAwaitingApproval(rows: readonly ObligationLike[]): { count: number; amount: number } {
  return rows.filter((o) => o.approval_status === 'pending_approval' && o.payment_status !== 'paid').reduce(
    (acc, o) => ({ count: acc.count + 1, amount: acc.amount + obligationTotal(o) }), { count: 0, amount: 0 });
}

/* -------------------------------- Billing -------------------------------- */

export interface BillingLike {
  total_logs: number | string;
  total_hours: number | string;
  billable_hours: number | string;
  total_amount: number | string;
}

export interface BillingTotals {
  /** Customers (rows) summed. */
  count: number;
  logs: number;
  hours: number;
  billableHours: number;
  amount: number;
}

export function sumBilling(rows: readonly BillingLike[]): BillingTotals {
  return rows.reduce<BillingTotals>((acc, b) => ({
    count: acc.count + 1,
    logs: acc.logs + Math.round(num(b.total_logs)),
    hours: acc.hours + num(b.total_hours),
    billableHours: acc.billableHours + num(b.billable_hours),
    amount: acc.amount + num(b.total_amount),
  }), { count: 0, logs: 0, hours: 0, billableHours: 0, amount: 0 });
}

/**
 * Hours worked but not billed (A6): total minus billable, never below 0
 * (rounding in the API can leave billable a hair above total).
 */
export function nonBillableHours(totalHours: number, billableHours: number): number {
  const v = totalHours - billableHours;
  return v > 0.0001 ? v : 0;
}

/**
 * Revenue per hour worked (A6): the billable amount over ALL hours worked,
 * non-billable included, so a customer who takes many free hours shows a lower
 * figure. `null` when no hours were worked (shown as "—", never ₪0).
 */
export function revenuePerHour(amount: number, totalHours: number): number | null {
  return totalHours > 0 ? amount / totalHours : null;
}

/* ------------------------------- Cash flow ------------------------------- */

export interface CashFlowLike {
  type: string;
  amount: number | string;
}

export interface CashFlowTotals {
  count: number;
  income: number;
  expense: number;
  /** income − expense */
  net: number;
}

/** Signed amount of one entry: income adds, expense subtracts. */
export const signedAmount = (e: CashFlowLike) => (e.type === 'income' ? num(e.amount) : -num(e.amount));

export function sumCashFlow(rows: readonly CashFlowLike[]): CashFlowTotals {
  const t = rows.reduce((acc, e) => {
    if (e.type === 'income') acc.income += num(e.amount);
    else acc.expense += num(e.amount);
    acc.count += 1;
    return acc;
  }, { count: 0, income: 0, expense: 0 });
  return { ...t, net: t.income - t.expense };
}

/* -------------------------------- Months --------------------------------- */

const pad = (n: number) => String(n).padStart(2, '0');

/** "2026-10" for the month of `d`. */
export const monthKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

/** The last `count` months, newest first ("2026-10", "2026-09", …). */
export function recentMonths(today: Date, count = 12): string[] {
  return Array.from({ length: count }, (_, i) => monthKey(new Date(today.getFullYear(), today.getMonth() - i, 1)));
}

/** First and last day of a "YYYY-MM" month (the obligations "issued this month" view). */
export function monthBounds(month: string): { from: string; to: string } | null {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return null;
  const [y, m] = month.split('-').map(Number);
  return { from: `${month}-01`, to: `${month}-${pad(new Date(y, m, 0).getDate())}` };
}

/** "2026-10" → "2026-10-01" (the billing API's month parameter). */
export const monthStart = (month: string) => `${month}-01`;
